import { useCallback, useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { useApplication } from "@/contexts/ApplicationContext";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { TabsContent } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
// Tabs look/behavior shared with Document Upload
import PersonalDetailsTabs from "@/components/application/personal-details/PersonalDetailsTabs";
import IdentityConfirmationSection from "./form-sections/IdentityConfirmationSection";
import VehicleReleaseForm from "./form-sections/VehicleReleaseForm";
import BankDetailsSection from "./form-sections/BankDetailsSection";
import NextOfKinSection from "./form-sections/NextOfKinSection";
import TaxDetailsSection from "./form-sections/TaxDetailsSection";
import AddressDetailsSection from "./form-sections/AddressDetailsSection";
import HeadshotUploadSection from "./form-sections/HeadshotUploadSection";
import { usePersonalDetailsSubmissionHandlers } from "@/hooks/usePersonalDetailsSubmissionHandlers";
import * as Sentry from "@sentry/react";
import { useActiveJob } from "@/contexts/ActiveJobContext";

const PersonalDetailsForm = () => {
  const { application } = useApplication();
  const { job } = useActiveJob();
  const [localApplication, setLocalApplication] = useState(application);
  const [activeTab, setActiveTab] = useState("identity-confirmation");
  const [bankProofFile, setBankProofFile] = useState<File | null>(null);
  const [bankProofPreview, setBankProofPreview] = useState<string | null>(null);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [headshotPreview, setHeadshotPreview] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const { toast } = useToast();
  // Track which tabs have been submitted
  const [submittedTabs, setSubmittedTabs] = useState<string[]>([]);
  // Track which tab is currently loading
  const [loadingTab, setLoadingTab] = useState<string | null>(null);

  // Resolve tax type from job config (read-only, defined at job level)
  const personalStage = job?.stages?.find((s) => s.id === "personal-details");
  const taxSegment = personalStage?.segments?.find(
    (seg) => (seg as unknown as { id?: string }).id === "tax-details"
  ) as unknown as
    | {
      id?: string;
      meta?: { taxTypes?: string[]; taxType?: string };
      taxType?: string;
    }
    | undefined;
  const resolvedTaxType =
    localApplication?.personalDetails?.company?.type || "";

  // Use our personal details submission handlers hook
  const {
    handleIdentitySubmit,
    handleBankDetailsSubmit,
    handleNextOfKinSubmit,
    handleTaxDetailsSubmit,
    handleAddressDetailsSubmit,
    handleHeadshotSubmit,
    handleFinalFormsSubmit,
  } = usePersonalDetailsSubmissionHandlers({
    // Identity props
    firstName: localApplication?.basicDetails?.firstName || "",
    lastName: localApplication?.basicDetails?.lastName || "",
    dateOfBirth: localApplication?.basicDetails?.dateOfBirth || "",
    sex: localApplication?.basicDetails?.sex || "",

    // Bank details props
    bankName: localApplication?.personalDetails?.bankName || "",
    accountNumber: localApplication?.personalDetails?.accountNumber || "",
    sortCode: localApplication?.personalDetails?.sortCode || "",
    accountName: localApplication?.personalDetails?.accountName || "",
    bankProofFile,
    bankProofPreview,

    // Next of kin props
    nextOfKinName: localApplication?.personalDetails?.nextOfKin?.name || "",
    nextOfKinPhone: localApplication?.personalDetails?.nextOfKin?.phone || "",
    nextOfKinRelation:
      localApplication?.personalDetails?.nextOfKin?.relation || "",

    // Tax details props
    nationalInsuranceNumber:
      localApplication?.personalDetails?.nationalInsuranceNumber || "",
    utrNumber: localApplication?.personalDetails?.utrNumber,
    companyType: resolvedTaxType,
    companyNumber: localApplication?.personalDetails?.company?.number,
    vatNumber: localApplication?.personalDetails?.company?.vatNumber,

    // Address details props
    addressLine1: localApplication?.personalDetails?.address?.line1 || "",
    addressLine2: localApplication?.personalDetails?.address?.line2,
    addressCity: localApplication?.personalDetails?.address?.city || "",
    addressPostcode: localApplication?.personalDetails?.address?.postcode || "",

    // Headshot props
    headshotFile,
    headshotPreview,

    // Final forms props
    signatureData: signature,
    // Navigation control
    setActiveTab,
  });

  // Helper function to get completed tabs based on application data
  const getCompletedTabs = useCallback(() => {
    const completedTabs = [];

    if (
      application.basicDetails.firstName &&
      application.basicDetails.lastName &&
      application.basicDetails.dateOfBirth &&
      application.basicDetails.sex
    ) {
      completedTabs.push("identity-confirmation");
    }

    if (
      application.personalDetails.bankName &&
      application.personalDetails.accountNumber &&
      application.personalDetails.sortCode &&
      application.personalDetails.accountName &&
      application.personalDetails.proofOfBankDetails
    ) {
      completedTabs.push("bank-details");
    }

    if (
      application.personalDetails.nextOfKin.name &&
      application.personalDetails.nextOfKin.phone &&
      application.personalDetails.nextOfKin.relation
    ) {
      completedTabs.push("next-of-kin");
    }

    if (application.personalDetails.nationalInsuranceNumber) {
      completedTabs.push("tax-details");
    }

    if (
      application.personalDetails.address.line1 &&
      application.personalDetails.address.city &&
      application.personalDetails.address.postcode
    ) {
      completedTabs.push("address-details");
    }

    if (application.documents.headshot) {
      completedTabs.push("headshot");
    }

    if (application.formsCompleted.vehicleReleaseForm) {
      completedTabs.push("vehicle-release-form");
    }

    return completedTabs;
  }, [application]);

  // Helper function to find the next incomplete tab
  const findNextIncompleteTab = useCallback(
    (completedTabs: string[]) => {
      const allTabs = [
        "identity-confirmation",
        "bank-details",
        "next-of-kin",
        "tax-details",
        "address-details",
        "headshot",
        // include final forms only when applicable
        ...(application.basicDetails.needsVan ? ["vehicle-release-form"] : []),
      ];

      for (const tab of allTabs) {
        if (!completedTabs.includes(tab)) {
          return tab;
        }
      }

      return ""; // Default to first tab if all are completed
    },
    [application.basicDetails.needsVan]
  );

  useEffect(() => {
    console.log("Application:", application);

    if (application.id) {
      const completedTabs = getCompletedTabs();
      console.log("Completed tabs:", completedTabs);

      // Derive visible tabs from job config (personal-details stage) with fallback
      const personalStage = job?.stages?.find(
        (s) => s.id === "personal-details"
      );
      const stageOrder = personalStage?.segments?.map((seg) => seg.id) || [];
      const fallbackOrder = [
        "identity-confirmation",
        "bank-details",
        "next-of-kin",
        "tax-details",
        "address-details",
        "headshot",
        "vehicle-release-form",
      ];
      let visibleTabs = stageOrder.length ? stageOrder : fallbackOrder;
      if (!visibleTabs.includes("identity-confirmation")) {
        visibleTabs = ["identity-confirmation", ...visibleTabs];
      }
      if (application.basicDetails.needsVan) {
        visibleTabs = [...visibleTabs];
      }

      // Prefer backend-provided current segment if it matches a visible tab
      const backendCurrentSegment =
        (application as unknown as Record<string, unknown>)[
        "current_segment"
        ] ||
        (application as unknown as Record<string, unknown>)["currentSegment"];

      let initialActiveTab = "";
      if (
        typeof backendCurrentSegment === "string" &&
        visibleTabs.includes(backendCurrentSegment)
      ) {
        initialActiveTab = backendCurrentSegment;
      } else {
        initialActiveTab = findNextIncompleteTab(completedTabs);
      }

      console.log("Initial active tab:", initialActiveTab);
      setSubmittedTabs(completedTabs);
      setActiveTab(initialActiveTab);
      setLocalApplication(application);
    }
  }, [application, job?.stages, getCompletedTabs, findNextIncompleteTab]); // Include all dependencies

  const validateTab = (tab: string): boolean => {
    console.log("Validating tab:", tab);
    switch (tab) {
      case "identity-confirmation":
        return !!(
          localApplication.basicDetails.firstName &&
          localApplication.basicDetails.lastName &&
          localApplication.basicDetails.dateOfBirth &&
          localApplication.basicDetails.sex
        );
      case "bank-details":
        return !!(
          localApplication.personalDetails.bankName &&
          localApplication.personalDetails.accountNumber &&
          localApplication.personalDetails.sortCode &&
          localApplication.personalDetails.accountName &&
          bankProofFile
        ); // Now requires proof of bank details
      case "next-of-kin":
        return !!(
          localApplication.personalDetails.nextOfKin.name &&
          localApplication.personalDetails.nextOfKin.phone &&
          localApplication.personalDetails.nextOfKin.relation
        );
      case "tax-details": {
        // Allowed types come from job config meta: taxTypes (or single taxType fallback)
        const TAX_TYPE_OPTIONS: Record<string, string> = {
          "sole-trader": "Sole Trader",
          "limited-company": "Limited Company",
          partnership: "Partnership",
          "not-applicable": "Not Applicable",
        };

        const resolvedTypes: string[] = (
          taxSegment?.meta?.taxTypes ??
          (taxSegment?.meta?.taxType ? [taxSegment.meta.taxType] : undefined) ??
          []
        ).filter(
          (t): t is string => typeof t === "string" && !!TAX_TYPE_OPTIONS[t]
        );
        const allowedTypes = resolvedTypes;

        const selectedType =
          localApplication?.personalDetails?.company?.type || "";
        const clampedType = allowedTypes.includes(selectedType)
          ? selectedType
          : "";

        // Must select a valid type from allowed options
        if (!clampedType) return false;

        // NIN always required and must be 2 letters, 6 numbers, 1 letter
        const nin = localApplication.personalDetails.nationalInsuranceNumber;
        if (!nin) return false;
        if (!/^[A-Z]{2}[0-9]{6}[A-Z]$/.test(nin)) return false;

        // UTR required for all except 'not-applicable' and must be exactly 10 digits
        if (clampedType !== "not-applicable") {
          const utrNumber = localApplication.personalDetails.utrNumber;
          if (!utrNumber) return false;
          const utrDigits = utrNumber.replace(/\D/g, "");
          if (utrDigits.length !== 10) return false;
        }

        // For limited company and partnership, company name is required; VAT optional (validate if provided)
        if (
          clampedType === "limited-company" ||
          clampedType === "partnership"
        ) {
          if (!localApplication.personalDetails.company?.number) return false;
          const vat = localApplication.personalDetails.company?.vatNumber;
          if (vat) {
            const vatDigits = vat.replace(/\D/g, "");
            if (vatDigits.length !== 9) return false;
          }
        }

        return true;
      }
      case "address-details":
        return !!(
          localApplication.personalDetails.address.line1 &&
          localApplication.personalDetails.address.city &&
          localApplication.personalDetails.address.postcode
        );
      case "headshot":
        return !!headshotFile || !!localApplication.documents.headshot;
      case "vehicle-release-form":
        return (
          !!signature || !!localApplication.formsCompleted.vehicleReleaseForm
        );
      default:
        return false;
    }
  };

  const handleTabChange = (value: string) => {
    // PersonalDetailsTabs already disables non-active triggers; just set state
    setActiveTab(value);
  };

  const handleTabSubmit = async (tab: string) => {
    if (!validateTab(tab)) {
      toast({
        title: "Required Fields",
        description: "Please complete all required fields before submitting",
        variant: "destructive",
      });
      return;
    }

    // Set loading state for this tab
    setLoadingTab(tab);

    try {
      // Move to next tab or complete if this is the last tab
      if (tab === "identity-confirmation") {
        await handleIdentitySubmit();
      } else if (tab === "bank-details") {
        await handleBankDetailsSubmit();
      } else if (tab === "next-of-kin") {
        await handleNextOfKinSubmit();
      } else if (tab === "tax-details") {
        await handleTaxDetailsSubmit();
      } else if (tab === "address-details") {
        await handleAddressDetailsSubmit();
      } else if (tab === "headshot") {
        await handleHeadshotSubmit();
      } else if (tab === "vehicle-release-form") {
        // Complete the entire process
        await handleFinalFormsSubmit();
      }

      // Mark tab as submitted only after successful submission
      setSubmittedTabs((prev) => {
        if (!prev.includes(tab)) {
          return [...prev, tab];
        }
        return prev;
      });

      // Get updated completed tabs including the one we just submitted
      const updatedCompletedTabs = getCompletedTabs();
      console.log("Updated completed tabs:", updatedCompletedTabs);
      // Find the next incomplete tab based on application data
      const nextTab = findNextIncompleteTab(updatedCompletedTabs);
      console.log("Next tab:", nextTab);
      console.log("Current tab:", tab);
      // Navigate to the next incomplete tab
      if (nextTab && nextTab !== tab) {
        setActiveTab(nextTab);
      }

      // Remove duplicate state update block
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "PersonalDetailsForm.handleTabSubmit",
          applicationId: application?.id,
          tab,
        },
      });
      console.error(`Error submitting ${tab}:`, error);
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      // Clear loading state
      setLoadingTab(null);
    }

    const pretty = tab.replace(/-/g, " ");
    toast({
      title: "Section Saved",
      description: `Your ${pretty} information has been saved`,
    });
  };

  const handleSignature = (signatureData: string | null) => {
    setSignature(signatureData);
    if (signatureData) {
      setLocalApplication((prev) => ({
        ...prev,
        formsCompleted: {
          ...prev.formsCompleted,
          vehicleReleaseForm: true,
        },
      }));
    }
  };

  // Old in-component tab list removed in favor of PersonalDetailsTabs

  // Render completion message or tabs based on activeTab
  if (activeTab === "") {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-green-800 mb-2">
            All Personal Details Submitted
          </h2>
          <p className="text-green-700 mb-4">
            You have successfully completed all personal details sections.
            Please contact administrators to be moved to the next stage.
          </p>
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
        </div>
      </div>
    );
  }
  // Render tabs if not all completed
  return (
    <div className="max-w-3xl mx-auto">
      <PersonalDetailsTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        submittedTabs={submittedTabs}
      >
        <TabsContent value="identity-confirmation">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <IdentityConfirmationSection
              application={localApplication}
              setApplication={setLocalApplication}
            />
            <Button
              onClick={() => handleTabSubmit("identity-confirmation")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={
                !validateTab("identity-confirmation") ||
                loadingTab === "identity-confirmation"
              }
            >
              {loadingTab === "identity-confirmation" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Confirm Identity"
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="bank-details">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <BankDetailsSection
              application={localApplication}
              setApplication={setLocalApplication}
              bankProofFile={bankProofFile}
              setBankProofFile={setBankProofFile}
              bankProofPreview={bankProofPreview}
              setBankProofPreview={setBankProofPreview}
            />
            <Button
              onClick={() => handleTabSubmit("bank-details")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={
                !validateTab("bank-details") || loadingTab === "bank-details" ||
                localApplication.personalDetails.accountName.length > 18
              }
            >
              {loadingTab === "bank-details" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Bank Details"
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="next-of-kin">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <NextOfKinSection
              application={localApplication}
              setApplication={setLocalApplication}
            />
            <Button
              onClick={() => handleTabSubmit("next-of-kin")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={
                !validateTab("next-of-kin") || loadingTab === "next-of-kin"
              }
            >
              {loadingTab === "next-of-kin" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Next of Kin Details"
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="tax-details">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <TaxDetailsSection
              application={localApplication}
              setApplication={setLocalApplication}
            />
            <Button
              onClick={() => handleTabSubmit("tax-details")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={
                !validateTab("tax-details") || loadingTab === "tax-details"
              }
            >
              {loadingTab === "tax-details" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Tax Details"
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="address-details">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <AddressDetailsSection
              application={localApplication}
              setApplication={setLocalApplication}
            />
            <Button
              onClick={() => handleTabSubmit("address-details")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={
                !validateTab("address-details") ||
                loadingTab === "address-details"
              }
            >
              {loadingTab === "address-details" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Address Details"
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="headshot">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <HeadshotUploadSection
              headshotFile={headshotFile}
              setHeadshotFile={setHeadshotFile}
              headshotPreview={headshotPreview}
              setHeadshotPreview={setHeadshotPreview}
            />
            <Button
              onClick={() => handleTabSubmit("headshot")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={!validateTab("headshot") || loadingTab === "headshot"}
            >
              {loadingTab === "headshot" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Headshot"
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="vehicle-release-form">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
            <VehicleReleaseForm
              onSign={handleSignature}
              signature={signature}
            />

            <Button
              onClick={() => handleTabSubmit("vehicle-release-form")}
              className="w-full mt-6 bg-honeycomb hover:bg-amber-500 text-white"
              disabled={!signature || loadingTab === "vehicle-release-form"}
            >
              {loadingTab === "vehicle-release-form" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirming...
                </>
              ) : (
                "Confirm Personal Details"
              )}
            </Button>
          </div>
        </TabsContent>
      </PersonalDetailsTabs>
    </div>
  );
};

export default PersonalDetailsForm;
